import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  ArrowLeftIcon, 
  CheckIcon,
  CalendarIcon,
  UserGroupIcon,
  FireIcon,
  ExclamationTriangleIcon,
  LockOpenIcon,
  LockClosedIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { SeasonService, ContestantService, PredictionService } from '../../../services/firebaseService';
import { ELIMINATION_TYPES } from '../../../models/dataModels';
import {
  isMultiEliminationWeek,
  isSimultaneousDoubleEliminationWeek,
} from '../../../utils/predictionUtils';

const schema = yup.object({
  airDate: yup.date().required('Air date is required'),
  isFinale: yup.boolean().required(),
  isMergeWeek: yup.boolean().required(),
  eliminationType: yup
    .string()
    .oneOf([
      ELIMINATION_TYPES.SINGLE,
      ELIMINATION_TYPES.DOUBLE,
      ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS,
      ELIMINATION_TYPES.TRIPLE,
    ])
    .required('Elimination type is required'),
  votedOut: yup.string().optional(),
  votedOut1: yup.string().optional(),
  votedOut2: yup.string().optional(),
  votedOut3: yup.string().optional(),
  winner: yup.string().optional(),
  fireMakingLoser: yup.string().optional(),
}).required();

export default function WeekDetail() {
  const { seasonId, weekNumber } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isOpenForPredictions, setIsOpenForPredictions] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [activePredictionCount, setActivePredictionCount] = useState(0);
  const [predictionCount, setPredictionCount] = useState(0);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      airDate: '',
      isFinale: false,
      isMergeWeek: false,
      eliminationType: 'single',
      votedOut: '',
      votedOut1: '',
      votedOut2: '',
      votedOut3: '',
      winner: '',
      fireMakingLoser: '',
    }
  });

  useEffect(() => {
    loadWeekData();
  }, [seasonId, weekNumber]);

  const loadWeekData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        setError('Season not found');
        return;
      }
      setSeason(seasonData);
      
      // Find the specific week
      const weekData = seasonData.weeks?.find(w => w.weekNumber === parseInt(weekNumber));
      if (!weekData) {
        setError('Week not found');
        return;
      }
      setWeek(weekData);
      setIsOpenForPredictions(weekData.isOpenForPredictions);
      
      // Load contestants for this season
      const seasonContestants = await ContestantService.getContestantsBySeason(seasonId);
      // Sort contestants alphabetically by name
      const sortedContestants = seasonContestants.sort((a, b) => a.name.localeCompare(b.name));
      setContestants(sortedContestants);
      
      // Load prediction count if week is open
      if (weekData.isOpenForPredictions && !weekData.results) {
        loadPredictionCount();
      }
      
      // Set form values using React Hook Form
      if (weekData.airDate) {
        try {
          // Handle both Firestore Timestamp and string dates
          const date = weekData.airDate.toDate ? weekData.airDate.toDate() : new Date(weekData.airDate);
          const formattedDate = date.toISOString().split('T')[0];
          setValue('airDate', formattedDate);
        } catch (error) {
          console.error('Error formatting air date:', error);
        }
      }
      
      // Set week properties
      setValue('isFinale', weekData.isFinale || false);
      setValue('isMergeWeek', weekData.isMergeWeek || false);
      setValue('eliminationType', weekData.eliminationType || 'single');
      
      // Set results
      if (weekData.results) {
        if (weekData.results.votedOut) {
          // Handle both old format (string/array) and new format (ordered array)
          if (Array.isArray(weekData.results.votedOut) && weekData.results.votedOut.length > 0 && weekData.results.votedOut[0].position) {
            // New ordered format for double eliminations
            const orderedResults = weekData.results.votedOut.sort((a, b) => a.position - b.position);
            if (orderedResults[0]) {
              // Use contestantName directly (already stored as name)
              setValue('votedOut1', orderedResults[0].contestantName);
            }
            if (orderedResults[1]) {
              // Use contestantName directly (already stored as name)
              setValue('votedOut2', orderedResults[1].contestantName);
            }
            if (orderedResults[2]) {
              setValue('votedOut3', orderedResults[2].contestantName);
            }
          } else if (Array.isArray(weekData.results.votedOut)) {
            // Old array format - convert ID to name if needed
            const contestant = contestants.find(c => c.id === weekData.results.votedOut[0]);
            setValue('votedOut', contestant ? contestant.name : weekData.results.votedOut[0] || '');
          } else {
            // Old string format - convert ID to name if needed
            const contestant = contestants.find(c => c.id === weekData.results.votedOut);
            setValue('votedOut', contestant ? contestant.name : weekData.results.votedOut);
          }
        }
        if (weekData.results.winner) {
          // Convert ID to name if needed
          const contestant = contestants.find(c => c.id === weekData.results.winner);
          setValue('winner', contestant ? contestant.name : weekData.results.winner);
        }
        if (weekData.results.fireMakingLoser) {
          // Convert ID to name if needed
          const contestant = contestants.find(c => c.id === weekData.results.fireMakingLoser);
          setValue('fireMakingLoser', contestant ? contestant.name : weekData.results.fireMakingLoser);
        }
      }
      
    } catch (error) {
      console.error('Error loading week data:', error);
      setError('Failed to load week data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      setError('');

      // Update the specific week
      const updatedWeeks = season.weeks.map(w => {
        if (w.weekNumber === parseInt(weekNumber)) {
          return {
            ...w,
            airDate: data.airDate,
            isFinale: data.isFinale,
            isMergeWeek: data.isMergeWeek,
            eliminationType: data.eliminationType,
            isOpenForPredictions,
            results: (() => {
              // Prepare results based on elimination type
              let results = {
                winner: data.winner || null,
                fireMakingLoser: data.fireMakingLoser || null,
              };

              if (isMultiEliminationWeek({ eliminationType: data.eliminationType })) {
                const votedOutOrdered = [];
                if (data.votedOut1) {
                  votedOutOrdered.push({ contestantName: data.votedOut1, position: 1 });
                }
                if (data.votedOut2) {
                  votedOutOrdered.push({ contestantName: data.votedOut2, position: 2 });
                }
                if (data.eliminationType === ELIMINATION_TYPES.TRIPLE && data.votedOut3) {
                  votedOutOrdered.push({ contestantName: data.votedOut3, position: 3 });
                }
                results.votedOut = votedOutOrdered.length > 0 ? votedOutOrdered : null;
              } else {
                // For single eliminations, store name directly
                results.votedOut = data.votedOut || null;
              }

              return results;
            })()
          };
        }
        return w;
      });

      // Update season with modified weeks
      await SeasonService.updateSeason(seasonId, {
        weeks: updatedWeeks
      });

      // Navigate back to season detail
      navigate(`/admin/seasons/${seasonId}`);
    } catch (error) {
      console.error('Error updating week:', error);
      setError('Failed to update week. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const loadPredictionCount = async () => {
    try {
      const predictions = await PredictionService.getPredictionsBySeasonAndWeek(
        seasonId,
        parseInt(weekNumber)
      );
      const activeCount = predictions.filter(p => !p.isLocked).length;
      const totalCount = predictions.length;
      setPredictionCount(activeCount);
    } catch (error) {
      console.error('Error loading prediction count:', error);
    }
  };

  const togglePredictionsStatus = async () => {
    // If currently open and trying to close, check for active predictions
    if (isOpenForPredictions) {
      try {
        // Count active (unlocked) predictions for this week
        const predictions = await PredictionService.getPredictionsBySeasonAndWeek(
          seasonId,
          parseInt(weekNumber)
        );
        const activeCount = predictions.filter(p => !p.isLocked).length;
        
        if (activeCount > 0) {
          // Show warning dialog
          setActivePredictionCount(activeCount);
          setShowCloseWarning(true);
          return;
        }
      } catch (error) {
        console.error('Error checking predictions:', error);
        // If error, proceed with toggle anyway
      }
    }
    
    // If closing with no active predictions, or opening, proceed immediately
    setIsOpenForPredictions(!isOpenForPredictions);
    // Reload prediction count if opening
    if (!isOpenForPredictions) {
      setTimeout(() => loadPredictionCount(), 500);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseWarning(false);
    setIsOpenForPredictions(false);
  };

  const handleCancelClose = () => {
    setShowCloseWarning(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/admin/seasons/${seasonId}`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Season
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/admin/seasons/${seasonId}`)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Week {week.weekNumber} Details
                  {week.isFinale && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Finale
                    </span>
                  )}
                  {week.isMergeWeek && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      Merge
                    </span>
                  )}
                  {week.eliminationType && (
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      isMultiEliminationWeek(week)
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {week.eliminationType === ELIMINATION_TYPES.TRIPLE
                        ? 'Triple'
                        : week.eliminationType === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS
                          ? 'Double (simultaneous)'
                          : week.eliminationType === ELIMINATION_TYPES.DOUBLE
                            ? 'Double'
                            : 'Single'}
                    </span>
                  )}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {season.name} • Manage week results and settings
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <CalendarIcon className="h-6 w-6 text-gray-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Week Management</h2>
              </div>
            </div>

            <div className="px-6 py-6">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                {/* Predictions Status */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-gray-900">Predictions Status</h3>
                        {isOpenForPredictions && (
                          <div className="ml-3 flex items-center">
                            <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                            <span className="ml-1 text-sm text-gray-600">
                              Hover for details
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Control whether players can submit predictions for this week
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        type="button"
                        onClick={togglePredictionsStatus}
                        className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-survivor-500 focus:ring-offset-2 ${
                          isOpenForPredictions ? 'bg-survivor-600' : 'bg-gray-200'
                        }`}
                        title={
                          isOpenForPredictions
                            ? `Open for predictions • ${predictionCount} active prediction${predictionCount !== 1 ? 's' : ''}${week?.airDate ? ` • Air date: ${new Date(week.airDate.toDate ? week.airDate.toDate() : week.airDate).toLocaleDateString()}` : ''}`
                            : 'Click to open predictions (players will be able to submit)'
                        }
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isOpenForPredictions ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    {isOpenForPredictions ? (
                      <div className="flex items-center text-green-600">
                        <LockOpenIcon className="h-5 w-5 mr-2" />
                        <span className="text-sm font-medium">Open for predictions</span>
                        {predictionCount > 0 && (
                          <span className="ml-3 text-sm text-gray-600">
                            ({predictionCount} active prediction{predictionCount !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <LockClosedIcon className="h-5 w-5 mr-2" />
                        <span className="text-sm font-medium">Closed for predictions</span>
                      </div>
                    )}
                    {week?.airDate && (
                      <div className="text-sm text-gray-500">
                        Air Date: {(() => {
                          try {
                            const date = week.airDate.toDate ? week.airDate.toDate() : new Date(week.airDate);
                            return date.toLocaleDateString();
                          } catch {
                            return 'N/A';
                          }
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Week Information */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <CalendarIcon className="h-6 w-6 text-gray-400 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Week Information</h3>
                  </div>
                  
                  <div>
                    <label htmlFor="airDate" className="block text-sm font-medium text-gray-700 mb-2">
                      Air Date *
                    </label>
                    <input
                      type="date"
                      id="airDate"
                      {...register('airDate')}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                        errors.airDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.airDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.airDate.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="eliminationType" className="block text-sm font-medium text-gray-700 mb-2">
                      Elimination Type *
                    </label>
                    <select
                      id="eliminationType"
                      {...register('eliminationType')}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                        errors.eliminationType ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value={ELIMINATION_TYPES.SINGLE}>Single Elimination</option>
                      <option value={ELIMINATION_TYPES.DOUBLE}>Double Elimination</option>
                      <option value={ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS}>
                        Double Elimination (Simultaneous)
                      </option>
                      <option value={ELIMINATION_TYPES.TRIPLE}>Triple Elimination</option>
                    </select>
                    {errors.eliminationType && (
                      <p className="mt-1 text-sm text-red-600">{errors.eliminationType.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Single: 1 pick. Double / simultaneous double: 2 ordered picks (order
                      matches predictions; simultaneous: booted players get no survival points;
                      others get two survival increments for the two departures). Triple: 3 ordered picks.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isMergeWeek"
                        {...register('isMergeWeek')}
                        className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isMergeWeek" className="ml-2 block text-sm text-gray-900">
                        Merge Week
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      Check this if this is the week when the merge occurs
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isFinale"
                        {...register('isFinale')}
                        className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isFinale" className="ml-2 block text-sm text-gray-900">
                        Finale Week
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      Check this if this is the final week of the season
                    </p>
                  </div>
                </div>

                {/* Results Section */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-6 w-6 text-gray-400 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Week Results</h3>
                  </div>
                  
                  {/* Voted Out */}
                  {isMultiEliminationWeek({ eliminationType: watch('eliminationType') }) ? (
                    <div className="space-y-4">
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                        <div className="flex items-center">
                          <FireIcon className="h-5 w-5 text-purple-600 mr-2" />
                          <span className="text-sm font-medium text-purple-800">
                            {watch('eliminationType') === ELIMINATION_TYPES.TRIPLE
                              ? 'Triple Elimination Week'
                              : isSimultaneousDoubleEliminationWeek({
                                  eliminationType: watch('eliminationType'),
                                })
                                ? 'Double Elimination (Simultaneous) Week'
                                : 'Double Elimination Week'}
                          </span>
                        </div>
                        <p className="text-xs text-purple-600 mt-1">
                          {isSimultaneousDoubleEliminationWeek({
                            eliminationType: watch('eliminationType'),
                          })
                            ? 'Positions #1 and #2 match prediction order. Booted players (either slot) get no survival points; drafted players still in the game get two survival increments (two people left).'
                            : `Set elimination order: #1 first voted out, then #2${
                                watch('eliminationType') === ELIMINATION_TYPES.TRIPLE
                                  ? ', then #3.'
                                  : '.'
                              }`}
                        </p>
                      </div>

                      <div>
                        <label htmlFor="votedOut1" className="block text-sm font-medium text-gray-700 mb-2">
                          Prediction #1 (First Voted Out)
                        </label>
                        <select
                          id="votedOut1"
                          {...register('votedOut1')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        >
                          <option value="">Select first person voted out</option>
                          {contestants.map((contestant) => (
                            <option key={contestant.id} value={contestant.name}>
                              {contestant.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="votedOut2" className="block text-sm font-medium text-gray-700 mb-2">
                          Prediction #2 (Second Voted Out)
                        </label>
                        <select
                          id="votedOut2"
                          {...register('votedOut2')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        >
                          <option value="">Select second person voted out</option>
                          {contestants.map((contestant) => (
                            <option key={contestant.id} value={contestant.name}>
                              {contestant.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {watch('eliminationType') === ELIMINATION_TYPES.TRIPLE && (
                        <div>
                          <label htmlFor="votedOut3" className="block text-sm font-medium text-gray-700 mb-2">
                            Prediction #3 (Third Voted Out)
                          </label>
                          <select
                            id="votedOut3"
                            {...register('votedOut3')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                          >
                            <option value="">Select third person voted out</option>
                            {contestants.map((contestant) => (
                              <option key={contestant.id} value={contestant.name}>
                                {contestant.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="votedOut" className="block text-sm font-medium text-gray-700 mb-2">
                        Voted Out
                      </label>
                      <select
                        id="votedOut"
                        {...register('votedOut')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                      >
                        <option value="">Select contestant voted out</option>
                          {contestants.map((contestant) => (
                            <option key={contestant.id} value={contestant.name}>
                              {contestant.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  {/* Finale-specific results */}
                  {week.isFinale && (
                    <div className="space-y-4 border-t border-gray-200 pt-6">
                      <h4 className="text-md font-medium text-gray-900">Finale Results</h4>
                      
                      <div>
                        <label htmlFor="winner" className="block text-sm font-medium text-gray-700 mb-2">
                          Winner
                        </label>
                        <select
                          id="winner"
                          {...register('winner')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        >
                          <option value="">Select winner</option>
                          {contestants.map((contestant) => (
                            <option key={contestant.id} value={contestant.name}>
                              {contestant.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="fireMakingLoser" className="block text-sm font-medium text-gray-700 mb-2">
                          Fire-Making Loser
                        </label>
                        <select
                          id="fireMakingLoser"
                          {...register('fireMakingLoser')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        >
                          <option value="">Select fire-making loser</option>
                          {contestants.map((contestant) => (
                            <option key={contestant.id} value={contestant.name}>
                              {contestant.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/seasons/${seasonId}`)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      'Updating...'
                    ) : (
                      <>
                        <CheckIcon className="h-4 w-4 mr-2" />
                        Update Week
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Close Warning Dialog */}
      {showCloseWarning && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Close Week for Predictions?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This week has <strong>{activePredictionCount}</strong> active prediction{activePredictionCount !== 1 ? 's' : ''} (not locked).
                    Are you sure you want to close it? Players will no longer be able to submit or modify predictions for this week.
                  </p>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleCancelClose}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmClose}
                      className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      Close Week
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
